import { useMemo } from 'react';

import {
  AutoHideInterfaceIcon,
  AutoScrollIcon,
  BracesIcon,
  BracketsIcon,
  CitationIcon,
  ClickToListenIcon,
  FootersIcon,
  FormulasIcon,
  HeadersIcon,
  ParenthesesIcon,
  TablesIcon,
  UrlIcon
} from 'components/newListeningExperience/settings/icons';
import FootNotesIcon from 'components/newListeningExperience/settings/icons/FootNotesIcon';
import { useTranslation } from 'hooks/useTypedTranslation';

import { DisplaySettings, ListeningSettings, SettingKey, SkipContentSettings } from '../settings';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type SettingItemConfig<K extends SettingKey> = {
  // Note: this is abstracted as function instead of hard coded constants to support the need to add localization supoprt down the line.
  settingDisplayName: () => string;
  icon: JSX.Element;
};

export type AutoSkipContentSettingItemConfig<K extends keyof SkipContentSettings> = SettingItemConfig<K> & {
  isDisabled?: (settings: Partial<ListeningSettings>) => boolean;
  disabledMessage?: () => string;
};

export const useSettingItemsUIConfig = () => {
  const { t } = useTranslation('common');
  const displaySettingsUIConfigs = useMemo<{
    [K in keyof Omit<DisplaySettings, 'darkMode'>]: SettingItemConfig<K>;
  }>(
    () => ({
      autoScroll: {
        settingDisplayName: () => t('Auto Scroll'),
        icon: <AutoScrollIcon className="w-5 h-5" />
      },
      clickToListen: {
        settingDisplayName: () => t('Click to Listen'),
        icon: <ClickToListenIcon className="w-5 h-5" />
      },
      autoHideInterface: {
        settingDisplayName: () => t('Auto-Hide Player'),
        icon: <AutoHideInterfaceIcon className="w-5 h-5" />
      }
    }),
    [t]
  );

  const autoSkipContentSettingsUIConfigs = useMemo<{
    [K in keyof Partial<SkipContentSettings>]: AutoSkipContentSettingItemConfig<K>;
  }>(
    () => ({
      // Note: these are ordered by the order of the settings in the settings page.
      headers: {
        settingDisplayName: () => t('Headers'),
        icon: <HeadersIcon />
      },
      footers: {
        settingDisplayName: () => t('Footers'),
        icon: <FootersIcon />
      },
      tables: {
        settingDisplayName: () => t('Tables'),
        icon: <TablesIcon />,
        isDisabled: settings => {
          if (settings.enhancedSkipping) {
            return false;
          }
          // disable when ML parsing is off
          return true;
        },
        disabledMessage: () => t('Available when “Enhanced Skipping” is on')
      },
      formulas: {
        settingDisplayName: () => t('Formulas'),
        icon: <FormulasIcon />
      },
      braces: {
        settingDisplayName: () => t('Braces'),
        icon: <BracesIcon />
      },
      citations: {
        settingDisplayName: () => t('Citations'),
        icon: <CitationIcon />
      },
      parentheses: {
        settingDisplayName: () => t('Parentheses'),
        icon: <ParenthesesIcon />
      },
      brackets: {
        settingDisplayName: () => t('Brackets'),
        icon: <BracketsIcon />
      },
      urls: {
        settingDisplayName: () => t('URLs'),
        icon: <UrlIcon />
      },
      footnotes: {
        settingDisplayName: () => t('Footnotes'),
        icon: <FootNotesIcon />
      }
    }),
    [t]
  );

  return { displaySettingsUIConfigs, autoSkipContentSettingsUIConfigs };
};
